import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { BaseContainer, NotificationType } from '@stylehaven/components';
import { AuthenticationSandbox } from '../../sandbox/authentication.sandbox';

@Component({
    selector: 'med-authentication',
    templateUrl: './auth-container.component.html',
    styleUrls: ['./auth-container.component.scss']
})
export class AuthContainerComponent extends BaseContainer implements OnInit {
    readonly notificationTypes: typeof NotificationType = NotificationType;
    userCredentials!: UntypedFormGroup;
    public oauthUrl$: Observable<string | undefined>;
    public showOauth$: Observable<boolean>;

    private entryNavigationUrl: string;

    constructor(
        private readonly sandbox: AuthenticationSandbox,
        private readonly router: Router,
        private readonly formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.setOauthUrl();
        this.setupForm();
    }

    private setOauthUrl(): void {
        this.entryNavigationUrl = this.route.snapshot.queryParamMap.get('entryNavigationUrl') ?? '/';
        this.oauthUrl$ = this.sandbox.getNewStatefulOauthUrl(this.entryNavigationUrl);
        this.showOauth$ = this.sandbox.hasExternalAuth();
    }

    private setupForm(): void {
        this.userCredentials = this.formBuilder.group({
            username: ['', Validators.required],
            password: ['', Validators.required]
        });

        this.loading$.pipe(takeUntil(this.destroy$)).subscribe(isLoading => {
            if (isLoading) {
                this.userCredentials.disable();
            } else {
                this.userCredentials.enable();
            }
        });
    }

    private setDirty(): void {
        this.userCredentials.controls.username.markAsDirty();
        this.userCredentials.controls.username.updateValueAndValidity();
        this.userCredentials.controls.password.markAsDirty();
        this.userCredentials.controls.password.updateValueAndValidity();
    }

    login(): void {
        this.setDirty();

        this.userCredentials.updateValueAndValidity();
        if (!this.userCredentials.valid) {
            return;
        }

        const { username, password }: { username: string; password: string } = this.userCredentials.value;
        this.setLoading(true);
        this.setError(false);

        this.sandbox
            .login(username, password)
            .pipe(
                catchError(_ => {
                    this.loginFailure();
                    return EMPTY;
                }),
                finalize(this.setLoading.bind(this, false))
            )
            .subscribe(() => {
                this.loginSuccess();
            });
    }

    private loginSuccess(): void {
        this.router.navigateByUrl(this.entryNavigationUrl);
    }

    private loginFailure(): void {
        this.setError(true);
    }
}
